import { memo, useState, useEffect, useMemo, useCallback, useRef, createElement } from '@max/max';
import View from '@hfe/max-view';
import Text from '@hfe/max-text';
import LImage from '@max/leez-image';
import Sensor from '@max/mbc-sensor';
import LinearGradientTitle from './LinearGradientTitle';
import './index.scss';
import ScrollView from '@hfe/max-scrollview';
import { useEngineContext } from '../../utils/utils';
import { LX_CONFIG } from '../../constants';
function extractLandmarkFromContent(content) {
    if (!content)
        return [];
    const result = [];
    const parts = content.split(/(<[^<>]+>)/);
    parts.forEach(part => {
        if (!part)
            return;
        // 替换 startsWith 和 endsWith
        const isLandmark = part.charAt(0) === '<' && part.charAt(part.length - 1) === '>';
        if (isLandmark) {
            result.push({
                type: 'landmark',
                content: part.substring(1, part.length - 1)
            });
        }
        else {
            result.push({
                type: 'text',
                content: part
            });
        }
    });
    return result;
}
// 通用处理换行符的函数
function normalizeNewlines(text) {
    if (!text)
        return '';
    // 递归处理所有可能的转义换行符
    let result = text;
    let prevResult = '';
    // 持续处理直到没有更多变化
    while (result !== prevResult) {
        prevResult = result;
        // 先处理 \\n 的情况
        result = result.replace(/\\\\n/g, '\n');
        // 再处理 \n 的情况
        result = result.replace(/\\n/g, '\n');
    }
    return result;
}
function ContentCard(props) {
    const ctx = useEngineContext();
    const { _LXReport, _moduleId } = ctx;
    const { aiAssistantData, cityName, cityId, onLandmarkClick, isContentShow = true, landmark, isCommonCityChangeAndSearch } = props;
    const { aiAssistantDecisionTextModel, isDisplay } = aiAssistantData;
    const aiDecisionInfo = aiAssistantDecisionTextModel || {};
    const { title, recommendText, recommendLandmarkModels = [] } = aiDecisionInfo;
    const [contentInfoList, setContentInfoList] = useState([]);
    const scrollViewRef = useRef(null);
    const reportedLandmarks = useRef(new Set());
    const cardsLayoutX = useRef([]);
    const pushCardsLayoutX = useCallback((x) => {
        cardsLayoutX.current.push(x);
    }, []);
    useEffect(() => {
        const contentInfoList = extractLandmarkFromContent(recommendText);
        setContentInfoList(contentInfoList);
    }, [recommendText]);
    let titleInfos = [];
    if (!aiAssistantDecisionTextModel) {
        titleInfos = [{
                type: 'landmark',
                content: `欢迎来到${cityName}`
            }];
    }
    else if (title) {
        titleInfos = extractLandmarkFromContent(title);
    }
    const handleLandmarkClick = useCallback((selectLandmark, isTextClick = false) => {
        var _a;
        if (isCommonCityChangeAndSearch)
            return;
        const targetLandmark = recommendLandmarkModels.find(landmarkItem => landmarkItem.landmark === selectLandmark);
        if (isTextClick) {
            _LXReport.mcReport(LX_CONFIG.AI_CONTENT_MC_BID, {
                valLab: {
                    _moduleId,
                    keyword: selectLandmark,
                    hot_spot: targetLandmark ? targetLandmark.landmark : ''
                }
            });
        }
        if (!targetLandmark)
            return;
        let finalLandmark = selectLandmark;
        if (landmark === selectLandmark) {
            // 如果与当前选中的landmark相同则视为取消
            finalLandmark = '';
        }
        onLandmarkClick && onLandmarkClick(finalLandmark);
        if (finalLandmark && isTextClick) {
            const cardLayoutInfo = cardsLayoutX.current.find((item) => item.landmark === finalLandmark);
            if (cardLayoutInfo) {
                (_a = scrollViewRef.current) === null || _a === void 0 ? void 0 : _a.scrollTo({
                    x: cardLayoutInfo.x,
                    animated: true,
                    duration: 300
                });
            }
        }
    }, [onLandmarkClick, recommendLandmarkModels, landmark, isCommonCityChangeAndSearch]);
    const onContentMv = () => {
        _LXReport.mvReport(LX_CONFIG.AI_CONTENT_MV_BID, {
            valLab: {
                _moduleId,
                city_id: cityId,
                city_name: cityName,
                expand_status: isDisplay ? 1 : 0
            }
        });
    };
    const onFilterCardMv = useCallback((landmark) => () => {
        if (reportedLandmarks.current.has(landmark)) {
            return;
        }
        _LXReport.mvReport(LX_CONFIG.HOTSPOT_MV_BID, {
            valLab: {
                _moduleId,
                hot_spot: landmark
            }
        });
        reportedLandmarks.current.add(landmark);
    }, [_moduleId]);
    const filterCardsList = useMemo(() => {
        if (!recommendLandmarkModels || recommendLandmarkModels.length === 0)
            return null;
        return recommendLandmarkModels.map((item, index) => {
            const isSelected = landmark === item.landmark;
            return <Sensor once key={`${item.landmark}-${index}`} onLayout={(event) => {
                var _a, _b;
                const x = (_b = (_a = event === null || event === void 0 ? void 0 : event.nativeEvent) === null || _a === void 0 ? void 0 : _a.layout) === null || _b === void 0 ? void 0 : _b.x;
                pushCardsLayoutX({
                    landmark: item.landmark,
                    x
                });
            }} onAppear={onFilterCardMv(item.landmark)}>
          <View className={isSelected ? ["filter-card-wrap", "filter-card-wrap-selected"] : "filter-card-wrap"} key={index} onClick={() => handleLandmarkClick(item.landmark)}>
            <View className="filter-card-title-wrap">
              <Text className={isSelected ? ["filter-card-title", "filter-card-title-selected"] : "filter-card-title"} numberOfLines={1}>{item.landmark}</Text>
              <LImage style={{
                width: '24rpx',
                height: '24rpx'
            }} source={{
                uri: isSelected ? 'https://p0.meituan.net/ingee/5129bed2d348858f2cd1db8342780bf1489.png' : 'https://p0.meituan.net/ingee/0d8dcfba7568c3b50d4e05fced922780343.png'
            }} showAnimatioin={false}/>
            </View>
            <Text className={isSelected ? ["filter-card-sub-text", "filter-card-sub-text-selected"] : "filter-card-sub-text"} numberOfLines={1}>{item.landmarkHotValue}</Text>
          </View>
        </Sensor>;
        });
    }, [recommendLandmarkModels, landmark]);
    // 当 recommendLandmarkModels 发生变化时，清空已上报记录
    useEffect(() => {
        reportedLandmarks.current.clear();
    }, [recommendLandmarkModels]);
    return <View className="content-wrap">
      <View className="indicator-arrow">
        <LImage className="" showAnimatioin={false} borderRadius={0} source={{
        uri: 'https://p0.meituan.net/ingee/655627c410e532925f2b4dea1fe23b23350.png'
    }}/>
      </View>
        <View className="content-title" style={{
        paddingBottom: isDisplay ? '12rpx' : '0rpx',
        marginBottom: props.isContentShow ? '8rpx' : '0rpx'
    }}>
          <LinearGradientTitle titleInfos={titleInfos}/>
          
        </View>
        {<Sensor onAppear={onContentMv}>
            
            {contentInfoList.length ? <View x-if={isContentShow} className="content-main">
                <Text className="content-text" numberOfLines={4}>
                  {contentInfoList.map((contentInfo, index) => {
        if (contentInfo.type === 'landmark') {
            return <Text key={index} className="landmark-text" onClick={() => handleLandmarkClick(contentInfo.content, true)}>
                          {contentInfo.content}
                        </Text>;
        }
        // 处理普通文本中的换行符
        if (contentInfo.content.includes('\n') || contentInfo.content.includes('\\n')) {
            // 使用通用函数处理所有可能的换行符情况
            const normalizedContent = normalizeNewlines(contentInfo.content);
            return normalizedContent.split('\n').map((line, lineIndex) => <Text key={`${index}-${lineIndex}`}>
                          {line}
                          {lineIndex < normalizedContent.split('\n').length - 1 && <Text>{'\n'}</Text>}
                        </Text>);
        }
        return contentInfo.content;
    })}
                </Text>
              </View> : <View x-if={isContentShow} className="content-main"><Text className="content-text">攻略正在生成中，很快就能见到它哦~</Text></View>}
            
            
            {!isCommonCityChangeAndSearch && (filterCardsList === null || filterCardsList === void 0 ? void 0 : filterCardsList.length) ? <ScrollView className="filter-card-scroll" ref={scrollViewRef} horizontal={true} showsHorizontalScrollIndicator={false}>
                {filterCardsList}
                <View className="scroll-empty-view"/>
              </ScrollView> : null}
          </Sensor>}
    </View>;
}
export default memo(ContentCard);
